import { styled } from '@mui/material';
import { useViewport } from 'contexts/viewport';
import { forwardRef, useEffect } from 'react';
import Box from 'shell/components/Box';
import Divider from 'shell/components/Divider';
import FormLabel from 'shell/components/FormLabel';
import Radio from 'shell/components/Radio';
import RadioGroup from 'shell/components/RadioGroup';
import Stack from 'shell/components/Stack';
import Tag from 'shell/components/Tag';
import Text from 'shell/components/Text';

const SubmitRubricTable = forwardRef(({ data, disabled, errors, form, onChange, ...rest }, ref) => {
    const { device } = useViewport();

    const totalMaxPoints = data
        .map((criterion) => {
            return Math.max(...criterion.options.map((option) => Number(option.rate)));
        })
        .reduce((acc, cur) => acc + cur, 0);

    const totalSelectedPoints = data.reduce((acc, cur) => {
        return (
            acc +
            cur.options.reduce((acc, cur) => {
                if (Object.values(form).includes(cur.id)) return acc + Number(cur.rate || 0);
                return acc;
            }, 0)
        );
    }, 0);

    const handleChange = (criterionId, optionId) => {
        const labelEl = document.getElementById(`${optionId}-label`);
        if (labelEl) {
            labelEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
        onChange(criterionId, optionId);
    };

    useEffect(() => {
        if (device === 'mobile') return;
        data.map(({ options }) => options)
            .flat()
            .filter(({ suggested }) => suggested)
            .map(({ id }) => id)
            .forEach((id) => {
                const child = document.getElementById(`${id}-label`);
                const parent = child.parentElement;
                const isParentScrollable = parent.scrollWidth > parent.clientWidth;
                if (isParentScrollable) {
                    const scrollAmount = child.getBoundingClientRect().left - parent.getBoundingClientRect().left;
                    parent.scrollLeft += scrollAmount;
                }
            });
    }, [device, data]);

    return (
        <RootStack ref={ref} {...rest}>
            <CustomBox>
                <Text variant={'p1'} weight={'semibold'}>
                    Criteria
                </Text>
            </CustomBox>
            <Stack>
                {data.map((criterion, criterionIndex) => {
                    const selectedOption = criterion.options.find(({ id }) => id === form?.[criterion.id]);
                    const criterionMaxPoints = Math.max(...criterion.options.map((option) => Number(option.rate)));
                    return (
                        <Stack key={criterion.id}>
                            <CriterionHeaderStack spacing={2.5}>
                                <Stack direction={'row'} justifyContent={'space-between'}>
                                    <Text variant={'p2'}>{criterion.title}</Text>
                                    <Text variant={'p2'}>
                                        Pts: {selectedOption?.rate || '-'}/{criterionMaxPoints}
                                    </Text>
                                </Stack>
                                <Text variant={'p3'}>{criterion.description}</Text>
                            </CriterionHeaderStack>
                            <OverflowStack
                                aria-label={`select a grade for criterion ${criterionIndex + 1}`}
                                component={RadioGroup}
                                device={device}
                                divider={
                                    <Divider flexItem orientation={device === 'mobile' ? 'horizontal' : 'vertical'} />
                                }
                                tabIndex={0}
                            >
                                {criterion.options.map((option) => (
                                    <CustomFormLabel key={option.id} id={`${option.id}-label`} htmlFor={option.id}>
                                        <Stack spacing={3}>
                                            <Stack direction={'row'} justifyContent={'space-between'}>
                                                <Stack sx={{ minWidth: 0 }} alignItems={'flex-start'} spacing={3}>
                                                    <Text data-cy={`rubric-points-${option.rate}`}>
                                                        {option.rate} pts
                                                    </Text>
                                                    <Text>{option.title}</Text>
                                                </Stack>
                                                <Stack direction={'row'} spacing={2}>
                                                    {option.suggested && <Tag type={'general'} value={'suggested'} />}
                                                    <CustomRadio
                                                        id={option.id}
                                                        checked={form?.[criterion.id] === option.id}
                                                        disabled={disabled}
                                                        onChange={() => handleChange(criterion.id, option.id)}
                                                    />
                                                </Stack>
                                            </Stack>
                                            <Text variant={'p3'}>{option.description}</Text>
                                        </Stack>
                                    </CustomFormLabel>
                                ))}
                            </OverflowStack>
                        </Stack>
                    );
                })}
            </Stack>
            <FooterBox>
                <Stack direction={'row'} divider={<Divider flexItem orientation={'vertical'} />}>
                    <CustomText sx={{ flex: 1 }} align={'right'} variant={'p2'}>
                        Total Points Out of {totalMaxPoints}
                    </CustomText>
                    <CustomText variant={'p2'}>{totalSelectedPoints}</CustomText>
                </Stack>
            </FooterBox>
        </RootStack>
    );
});

const RootStack = styled(Stack)(({ theme }) => {
    return {
        border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderRadius: '12px',
    };
});

const CustomBox = styled(Box)(({ theme }) => {
    return {
        borderRadius: '12px 12px 0 0',
        padding: theme.spacing(3),
        background: theme.palette.ensightful.primary[50],
    };
});

const CriterionHeaderStack = styled(Stack)(({ theme }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderBottom: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        padding: theme.spacing(2.5),
        background: theme.palette.ensightful.neutral[100],
    };
});

const OverflowStack = styled(Stack)(({ device, theme }) => {
    return {
        flexDirection: device === 'mobile' ? 'column' : 'row',
        flexWrap: 'unset',
        overflowX: 'auto',
        '&:focus-visible': {
            zIndex: 1,
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const CustomFormLabel = styled(FormLabel)(({ theme }) => {
    return {
        flex: 1,
        minWidth: '240px',
        minHeight: '100%',
        padding: theme.spacing(3),
        cursor: 'pointer',
    };
});

const CustomRadio = styled(Radio)(({ theme }) => {
    return {
        alignSelf: 'flex-start',
        margin: theme.spacing(-2),
    };
});

const FooterBox = styled(Box)(({ theme }) => {
    return {
        borderTop: `1px solid ${theme.palette.ensightful.neutral[300]}`,
        borderRadius: '0 0 12px 12px',
        background: theme.palette.ensightful.primary[50],
    };
});

const CustomText = styled(Text)(({ theme }) => {
    return {
        padding: theme.spacing(4),
    };
});

export default SubmitRubricTable;
